interface SpamFields {
  spamvalidation: string;
  yourLastName: string;
}

export interface OtpErrorMessages {
  generic: string;
  rateLimited: string;
}

interface RequestCodeResult {
  success: boolean;
  errorMessage?: string;
}

export async function requestLoginCode(
  email: string,
  csrfToken: string,
  altchaPayload: string,
  spamFields: SpamFields | undefined,
  errors: OtpErrorMessages,
): Promise<RequestCodeResult> {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("_csrf_token", csrfToken);
  formData.append("altcha", altchaPayload);

  if (spamFields) {
    formData.append("spamvalidation", spamFields.spamvalidation);
    formData.append("your_last_name", spamFields.yourLastName);
  }

  const response = await fetch("/website-api/auth/login-code/request/", {
    credentials: "same-origin",
    method: "post",
    body: formData,
  });

  if (response.status === 429) {
    return {
      success: false,
      errorMessage: errors.rateLimited,
    };
  }

  if (response.status === 403) {
    const json = await response.json().catch(() => null);
    return {
      success: false,
      errorMessage: json?.error || errors.generic,
    };
  }

  const jsonResponse = await response.json();

  if (jsonResponse.metadata.code >= 400) {
    return {
      success: false,
      errorMessage: jsonResponse.metadata.error_message || errors.generic,
    };
  }

  return { success: true };
}

interface VerifyCodeResult {
  success: boolean;
  redirect?: string;
  errorMessage?: string;
}

export async function verifyLoginCode(
  email: string,
  code: string,
  csrfToken: string,
  errors: OtpErrorMessages,
): Promise<VerifyCodeResult> {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("code", code);
  formData.append("_csrf_token", csrfToken);

  const response = await fetch("/website-api/auth/login-code/verify/", {
    credentials: "same-origin",
    method: "post",
    body: formData,
  });

  if (response.status === 429) {
    return {
      success: false,
      errorMessage: errors.rateLimited,
    };
  }

  const jsonResponse = await response.json();

  if (jsonResponse.success) {
    return { success: true, redirect: jsonResponse.redirect };
  }

  return {
    success: false,
    errorMessage: jsonResponse.error || jsonResponse.metadata?.error_message || errors.generic,
  };
}

export interface CodeInputController {
  disable(): void;
  enable(): void;
  reset(): void;
}

export function setupCodeInput(
  container: HTMLElement,
  onComplete: (code: string) => void,
  onInput?: () => void,
): CodeInputController {
  const input = container.querySelector(".code-input") as HTMLInputElement;
  const slots = Array.from(container.querySelectorAll<HTMLElement>(".code-slot"));

  function updateSlots() {
    const value = input.value;
    slots.forEach((slot, i) => {
      slot.textContent = value[i] || "";
      const isFocused = document.activeElement === input;
      slot.classList.toggle("active", isFocused && i === value.length && value.length < 6);
    });
  }

  input.addEventListener("input", () => {
    input.value = input.value.replace(/\D/g, "").slice(0, 6);
    updateSlots();
    onInput?.();

    if (input.value.length === 6) {
      onComplete(input.value);
    }
  });

  // Clicking visual slots focuses the real input
  container.querySelector(".code-display")?.addEventListener("click", () => {
    input.focus();
  });

  input.addEventListener("focus", updateSlots);
  input.addEventListener("blur", () => {
    slots.forEach((slot) => {
      slot.classList.remove("active");
    });
  });

  return {
    disable() {
      input.disabled = true;
    },
    enable() {
      input.disabled = false;
    },
    reset() {
      input.disabled = false;
      input.value = "";
      updateSlots();
      setTimeout(() => input.focus(), 10);
    },
  };
}
